import React from "react";
import { Box, Typography, Button, Container, useTheme } from "@mui/material";
import {
  HomeOutlined, // Home
  MenuBookOutlined, // Documentation
  HelpOutlineOutlined, // Support
  ArrowBack, // ArrowLeft
  ErrorOutline, // AlertCircle
} from "@mui/icons-material";

const NotFoundPage = () => {
  const theme = useTheme();

  const handleGoBack = () => {
    window.history.back();
  };

  const quickLinks = [
    { icon: HomeOutlined, label: "Home", href: "/" },
    { icon: MenuBookOutlined, label: "Documentation", href: "/docs" },
    { icon: HelpOutlineOutlined, label: "Support", href: "/support" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        p: 2,
      }}
    >
      <Container
        maxWidth="md"
        sx={{ textAlign: "center", position: "relative", zIndex: 1 }}
      >
        {/* --- 404 BIG NUMBER --- */}
        <Box sx={{ position: "relative", mb: 4, display: "inline-block" }}>
          {/* Main Gradient Text */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "6rem", md: "9rem" },
              fontWeight: 900,
              lineHeight: 1,
              background: theme.appGradients.warning,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              position: "relative",
              zIndex: 2,
            }}
          >
            404
          </Typography>

          {/* Blurred Glow Behind */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "6rem", md: "9rem" },
              fontWeight: 900,
              lineHeight: 1,
              color: theme.palette.primary.main,
              position: "absolute",
              top: 0,
              left: 0,
              opacity: 0.3,
              filter: "blur(20px)",
              zIndex: 1,
              width: "100%",
            }}
          >
            404
          </Typography>
        </Box>

        {/* --- ERROR ICON --- */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 80,
              height: 80,
            }}
          >
            {/* Background Circle */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                bgcolor: theme.palette.error.main, // Semantic error color
                opacity: 0.15,
              }}
            />
            <ErrorOutline
              sx={{ fontSize: 48, color: theme.palette.error.main }}
            />
          </Box>
        </Box>

        {/* --- TEXT CONTENT --- */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            Oops! Page Not Found
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: 480,
              mx: "auto",
              fontSize: "1.1rem",
              lineHeight: 1.6,
            }}
          >
            The page you're looking for seems to have vanished into the digital
            void. Don't worry though – we'll help you find your way back.
          </Typography>
        </Box>

        {/* --- ACTION BUTTON --- */}
        <Box>
          <Button
            onClick={handleGoBack}
            startIcon={<ArrowBack />}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: theme.appRadius.lg,
              bgcolor: theme.palette.neutral.dark, // Dark grey button like Tailwind sample
              color: theme.palette.common.white,
              fontWeight: 600,
              "&:hover": {
                bgcolor: theme.palette.neutral.main,
                boxShadow: theme.shadows[4],
              },
            }}
          >
            Go Back
          </Button>
        </Box>

        {/* --- FOOTER TEXT --- */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="caption" color="text.disabled">
            Error Code: 404 • Page Not Found
          </Typography>
        </Box>
      </Container>

      {/* --- BACKGROUND BLOBS (Static Decorations) --- */}
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -50,
            right: -50,
            width: 300,
            height: 300,
            borderRadius: "50%",
            bgcolor: theme.palette.primary.main,
            opacity: 0.08,
            filter: "blur(80px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -50,
            left: -50,
            width: 300,
            height: 300,
            borderRadius: "50%",
            bgcolor: theme.palette.secondary.main,
            opacity: 0.08,
            filter: "blur(80px)",
          }}
        />
      </Box>
    </Box>
  );
};

export default NotFoundPage;
